import {
  FaultLog1Type,
  LogIndex,
  getClockAdjEpochs,
  makeRecord,
} from 'pikaparam';

import {CHAMBER_LOG_VALUES_DICTIONARY, Constants} from '../../../../constants';
import {ChamberType} from '../../../types';
import {meetsMinimumFirmware} from '../../../utils';
import {FlatProperties} from '../pikaparam';
import {FirmwareType, IPeakDevice} from './IPeakDevice';
import {PeakDeviceBase, PeakDeviceBaseOptions} from './PeakDeviceBase';

export class PeakFlatDevice
  extends PeakDeviceBase<FlatProperties>
  implements IPeakDevice
{
  constructor(o: Omit<PeakDeviceBaseOptions, 'firmwareType'>) {
    super({...o, firmwareType: FirmwareType.Flat});
  }

  protected async readChamberType(): Promise<ChamberType> {
    const supported = meetsMinimumFirmware(
      this.softwareRevision,
      Constants.MINIMUM_FIRMWARE_VERSION.CHAMBER_TYPE,
    );

    if (supported) {
      const value = await this.client.read<number>('heaterType');

      if (!Object.values(ChamberType).includes(value)) return ChamberType.None;

      return value as ChamberType;
    }

    const faultEndIndex = supported
      ? undefined
      : await this.client.read<number>('faultLogEnd');

    try {
      let currentTemp = await this.client.read<number>('heaterTemp');

      if (faultEndIndex === undefined)
        return currentTemp ? ChamberType.Classic : ChamberType.None;

      await this.client.write<number>(
        'faultLogPointer',
        Math.max(faultEndIndex - 1, 0),
      );

      const faultLog = makeRecord(
        LogIndex.Fault,
        {
          index: faultEndIndex - 1,
          buffer: await this.client.read<ArrayBuffer>('faultLogEntry'),
        },
        0,
        getClockAdjEpochs(LogIndex.Fault, []),
      );

      currentTemp = await this.client.read<number>('heaterTemp');

      if (
        faultLog.typeCode !== FaultLog1Type.HeaterTempLost ||
        !faultLog.freeze.length
      ) {
        return currentTemp ? ChamberType.Classic : ChamberType.None;
      }

      const rawCode = parseInt(faultLog.freeze[0].value);

      if (typeof rawCode !== 'number')
        return currentTemp ? ChamberType.Classic : ChamberType.None;

      return (
        [ChamberType.Performance, ChamberType.XL].find(type => {
          const logValues = CHAMBER_LOG_VALUES_DICTIONARY[type];
          return (
            logValues &&
            rawCode >= logValues.minValue &&
            rawCode <= logValues.maxValue
          );
        }) ?? ChamberType.None
      );
    } catch {
      return ChamberType.None;
    }
  }

  async stopDabbing(): Promise<void> {
    await this.client.write<number>(
      'modeCmd',
      Constants.MODE_COMMAND.heatCycleAbort,
    );
    await this.client.write<number>('modeCmd', Constants.MODE_COMMAND.idle);
  }
}
